import { action }         from 'mobx';
import { makeObservable } from 'mobx';
import { observable }     from 'mobx';
import modelDictionary    from '@widesk-core/models/ModelDictionary';
import { getIdFromUrn }   from '@widesk-core/tools/identifier';

/**
 * Nom des auteurs de modifications, résolus depuis leur seule URN et gardés en mémoire.
 *
 * Sans ça, on ne savait nommer que les utilisateurs connectés en ce moment sur le projet : quelqu'un
 * qui avait modifié puis fermé l'éditeur s'affichait "par un autre utilisateur". C'est la même
 * mécanique que le trait `Blamable` des modèles (`ModelDictionary` associe le préfixe d'une URN à sa
 * classe de modèle), mais utilisable sur une URN isolée, hors de tout modèle porteur.
 *
 * Les échecs sont mémorisés comme les succès : un compte supprimé ou hors de nos droits ne doit pas
 * relancer une requête à chaque rendu.
 */
class AuthorNameStore {
	private readonly _names = observable.map<string, string>();

	// Hors mobx : purement technique (éviter deux requêtes simultanées pour la même URN), aucun rendu
	// ne dépend de cette information.
	private readonly _pending = new Set<string>();

	public constructor() {
		makeObservable(this);
	}

	/**
	 * Le nom si on le connaît déjà, `undefined` sinon — auquel cas la résolution part en arrière-plan
	 * et le composant qui lit cette valeur se redessinera de lui-même une fois le nom connu.
	 */
	public get(urn: Urn): string | undefined {
		const known = this._names.get(urn);

		if (known !== undefined) return known || undefined;

		void this._resolve(urn);

		return undefined;
	}

	private async _resolve(urn: Urn) {
		if (this._pending.has(urn) || this._names.has(urn)) return;

		this._pending.add(urn);

		try {
			const ModelClass = modelDictionary.get(urn);

			// URN d'un type qu'aucun modèle ne déclare : rien à tenter, et surtout rien à retenter.
			if (!ModelClass) return this._setName(urn, '');

			const model = new ModelClass({ id: getIdFromUrn(urn) }) as { fetch: (options: { noError: boolean }) => Promise<unknown>; fullName?: string };

			await model.fetch({ noError: true });

			this._setName(urn, model.fullName?.trim() || '');
		} catch (err) {
			this._setName(urn, '');
			console.error(`Résolution de l'auteur "${urn}"`, err);
		} finally {
			this._pending.delete(urn);
		}
	}

	/** La chaîne vide vaut "cherché, pas trouvé" — distinct de "jamais cherché" (absent de la table). */
	@action private _setName(urn: string, name: string) {
		this._names.set(urn, name);
	}
}

export default new AuthorNameStore();
